import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { initMapFactory, initMultiMapFactory, queryMapFactory, DefaultFailureAction, DefaultSuccessAction, dataHookWithCredentialsFactory, readonlyHookWithCredentialsFactory, compareArray, memoizeWithKey } from "../../common/reducers/utilities";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { ensureInteger } from "../../common/utils/generalUtils";
import { canMgrViewEvents } from "../event/eventMgrSlice";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = group => ensureInteger(group.id);

export const shiftGroupMgrSlice = createSlice({
    name: 'shiftGroups',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            ensureArray(action.payload.ids).forEach(id => delete state.data[id]);
            ensureArray(action.payload.data).forEach(group => state.data[key(group)] = group);
        },
        setForShift: (state, action) => {
            const toProcess = new Set()
            Object.values(state.data).forEach(g => {
                if (action.payload.shiftids.includes(g.shiftid)) {
                    toProcess.add(key(g));
                }
            })
            action.payload.data.forEach(g => {
                state.data[key(g)] = g;
                toProcess.delete(key(g));
            });
            toProcess.forEach(id => delete state.data[id]);
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(g => {
                state.data[key(g)] = g;
            })
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(g => {
                delete state.data[key(g)];
            })
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => shiftGroupMgrSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'shiftGroup_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'shiftGroup_one');

const fetchShiftGroup = aggregatingFetch(
    id => network => network.get('/api/v2/shiftGroup/' + encodeURIComponent(id), data => shiftGroupMgrSlice.actions.set({ data, ids: [id] }), visualizeFailure('get')),
    ids => network => network.post('/api/v2/shiftGroups', ids, data => shiftGroupMgrSlice.actions.set({ data, ids }), visualizeFailure('get')),
    { primitiveKey: true }
);
const fetchShiftGroupsForShift = aggregatingFetch(
    shiftid => network => network.get('/api/v2/shiftGroups/' + encodeURIComponent(shiftid), data => shiftGroupMgrSlice.actions.setForShift({ shiftids: [shiftid], data }), visualizeFailure('get')),
    shiftids => network => network.post('/api/v2/shiftGroupsForShifts', shiftids, data => shiftGroupMgrSlice.actions.setForShift({ shiftids, data }), visualizeFailure('get')),
    { primitiveKey: true }
);
export const createShiftGroup = (network, group) => network.post('/api/v1/shiftGroup', group, shiftGroupMgrSlice.actions.addOrUpdate, visualizeFailure('create'));
export const updateShiftGroup = (network, delta) => network.put('/api/v1/shiftGroup', delta, shiftGroupMgrSlice.actions.addOrUpdate, visualizeFailure('update'));
export const removeShiftGroup = (network, id, mode) => network.delete('/api/v1/shiftGroup/' + encodeURIComponent(id) + (mode ? '/' + mode : ''), shiftGroupMgrSlice.actions.remove, visualizeFailure('remove'));
// #endregion

// #region selectors
const selectShiftGroupData = state => state.shiftGroups.data;
const selectShiftGroup = id => createSelector([selectShiftGroupData], groups => groups[id]);
const selectShiftGroups = ids => createSelector([selectShiftGroupData], groups => ids.reduce((res, id) => {
    if (groups[id]) {
        return res.concat(groups[id])
    } 
    return res;
}, []));
const selectShiftGroupsForShift = memoizeWithKey(shiftidInputs => {
    const shiftids = new Set(ensureArray(shiftidInputs));
    return createSelector([selectShiftGroupData], groups => {
        return Object.values(groups).filter(g => shiftids.has(g.shiftid));
    });
}, shiftidInputs => ensureArray(shiftidInputs).join(','));
// #endregion

const listen = {
    groupCreated: (group, { dispatch, websocket }) => {
        markFetched(websocket, 'shift_group', group.id);
        dispatch(shiftGroupMgrSlice.actions.addOrUpdate(group))
    },
    groupUpdated: (group, { dispatch }) => dispatch(shiftGroupMgrSlice.actions.addOrUpdate(group)),
    groupRemoved: (group, { dispatch }) => dispatch(shiftGroupMgrSlice.actions.remove(group))
}

const options = {
    cleanup: { root: shiftGroupMgrSlice.name, all: state => Object.values(selectShiftGroupData(state)), removeAll: shiftGroupMgrSlice.actions.remove, key },
    condition: canMgrViewEvents
}

const optionsSingle = { ...options, defaultValue: null}

const shiftGroupInitByID = initMapFactory(fetchShiftGroup, listen, { name: 'shiftGroupByID' });
registerEntityBasedFetch('shift_group', shiftGroupInitByID);

export const [useMgrShiftGroup, realizeMgrShiftGroup] = dataHookWithCredentialsFactory(selectShiftGroup, shiftGroupInitByID, optionsSingle);
export const [useMgrShiftGroups] = dataHookWithCredentialsFactory(selectShiftGroups, initMultiMapFactory(shiftGroupInitByID, listen), { ...options, comparator: compareArray });

const shiftGroupInitByShift = queryMapFactory(fetchShiftGroupsForShift, 'id', shiftGroupInitByID, listen, { name: 'shiftGroupForShift' });

export const [useMgrShiftGroupsForShift, realizeMgrShiftGroupsForShift] = dataHookWithCredentialsFactory(selectShiftGroupsForShift, shiftGroupInitByShift, { ...options, comparator: compareArray});
export const [useMgrShiftGroupsForShifts] = dataHookWithCredentialsFactory(selectShiftGroupsForShift, initMultiMapFactory(shiftGroupInitByShift, listen, { name: 'shiftGroupsForShifts' }), { ...options, comparator: compareArray });
export const realizeMgrShiftGroupsForShifts = async (context, ids) => (await Promise.all(ids.map(id => realizeMgrShiftGroupsForShift(context, id)))).flat();

export const [useReadMgrShiftGroup] = readonlyHookWithCredentialsFactory(selectShiftGroup, shiftGroupInitByShift, optionsSingle);
export const [useReadMgrShiftGroups] = readonlyHookWithCredentialsFactory(selectShiftGroups, initMultiMapFactory(shiftGroupInitByShift, listen, { name: 'shiftGroupsForShifts' }), options);

const reducer = { name: shiftGroupMgrSlice.name, reducer: shiftGroupMgrSlice.reducer }
export default reducer;